import { buildTimeline, monthEnd, monthStart, positionInterval } from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoPeriodo,
  CargaTrabalhoProfissionaisData,
  CargaTrabalhoProfissional,
  GanttIndicators,
  IsoDate,
  Timeline,
} from "@/modules/carga-trabalho";
import type { Profissional } from "@/modules/recursos";
import {
  buildProfissionalRows,
  isAlocacaoVisivel,
  obraCardTitle,
  obraLabel,
  periodoText,
  rangeLine,
  tipCardToText,
} from "@/shared/components/gantt-carga/gantt-rows";
import type { GanttRowModel, GanttTipCard } from "@/shared/components/gantt-carga/gantt-rows";
import { REVISAO_LEGEND, REVISAO_LEGEND_SIMULACAO } from "../../resolver-conflito.constants";
import { periodoObra } from "../../resolver-conflito.utils";

/** Cancelada segue o indicador da tela (bug 3413): a revisão não mostra o que o Gantt da tela esconde. */
function indicadoresDaRevisao(canceladaVisivel: boolean): GanttIndicators {
  return {
    planejamento: true,
    obra: true,
    fechamento: true,
    cancelada: canceladaVisivel,
    indisponibilidade: true,
  };
}

/** Legenda da revisão: sem "Cancelada" quando o indicador está desligado. */
export function legendaDaRevisao(simulando: boolean, canceladaVisivel: boolean) {
  return (simulando ? REVISAO_LEGEND_SIMULACAO : REVISAO_LEGEND).filter(
    (item) => canceladaVisivel || item.key !== "cancelada",
  );
}

/** Do mês do início ao mês do fim do Período da Obra (e da alocação), para a obra caber à vista. */
function janelaDaObra(alocacao: CargaTrabalhoAlocacao): CargaTrabalhoPeriodo {
  const obra = periodoObra(alocacao.obra);
  const inicio = [obra?.inicio, alocacao.inicio].filter(Boolean).sort()[0] ?? alocacao.inicio;
  const fim = [obra?.fim, alocacao.fim].filter(Boolean).sort().reverse()[0] ?? alocacao.fim;
  return { inicio: monthStart(inicio.slice(0, 7)), fim: monthEnd(fim.slice(0, 7)) };
}

const TRANSFERIDA_KEY_SUFFIX = "-transferida";

export interface RevisaoGanttInput {
  /** Profissional em conflito, como veio da Carga de Trabalho. */
  profissional: CargaTrabalhoProfissional;
  /** Alocação que passará ao substituto. */
  alocacao: CargaTrabalhoAlocacao;
  substituto: Profissional;
  /** Carga de trabalho carregada na tela: fonte das alocações do substituto. */
  carga: CargaTrabalhoProfissionaisData | null;
  periodo: CargaTrabalhoPeriodo;
  today: IsoDate;
  canceladaVisivel: boolean;
}

/**
 * Alocações do substituto: as da carga carregada, quando ele aparece nela; senão,
 * só as indisponibilidades informadas pela listagem de profissionais (HU 1761).
 */
function substitutoNaCarga(input: RevisaoGanttInput): CargaTrabalhoProfissional {
  const { substituto, carga } = input;
  const encontrado = carga?.profissionais.find((item) => item.id === substituto.id);
  if (encontrado) return { ...encontrado, conflitosIgnorados: [] };
  return {
    id: substituto.id,
    nome: substituto.nome,
    time: substituto.cargo || substituto.time || null,
    alocacoes: [],
    indisponibilidades: substituto.indisponibilidade.periodos,
  };
}

/**
 * Gantt da etapa Revisão (HU 2434): uma linha para o profissional substituído e
 * outra para o substituto, com a obra transferida tracejada e o período da
 * alocação em destaque nas duas.
 */
export function buildRevisaoGantt(input: RevisaoGanttInput): {
  rows: GanttRowModel[];
  timeline: Timeline;
} {
  const { profissional, alocacao, periodo, today, canceladaVisivel } = input;
  const substituto = substitutoNaCarga(input);
  const transferidaId = `${alocacao.id}${TRANSFERIDA_KEY_SUFFIX}`;

  const data: CargaTrabalhoProfissionaisData = {
    periodo,
    profissionais: [
      { ...profissional, conflitosIgnorados: [] },
      {
        ...substituto,
        alocacoes: [...substituto.alocacoes, { ...alocacao, id: transferidaId }],
      },
    ],
  };

  const timeline = buildTimeline(janelaDaObra(alocacao), alocacao.inicio);
  const highlight = positionInterval(alocacao, timeline);
  const indicadores = indicadoresDaRevisao(canceladaVisivel);
  // A obra transferida ainda não conta como alocação do substituto; a contagem segue o indicador, como a tela.
  const contadas = [profissional, substituto].map(
    (item) => item.alocacoes.filter((a) => isAlocacaoVisivel(a.obra, indicadores)).length,
  );
  const rows = buildProfissionalRows(data, timeline, indicadores, today).map((row, index) => {
    const alocacoes = contadas[index] ?? 0;
    return {
      ...row,
      meta: `${alocacoes} ${alocacoes === 1 ? "Alocação" : "Alocações"}`,
      conflicts: 0,
      conflictTooltip: "",
      conflitos: [],
      items: row.items.map((item) => {
        if (item.key !== transferidaId) return item;
        const position = positionInterval(alocacao, timeline);
        const card: GanttTipCard = {
          title: obraCardTitle(alocacao.obra),
          subtitle: item.card?.subtitle ?? null,
          badge: "Obra Transferida",
          badgeTone: "neutral",
          phases: [],
          facts: [{ label: "Alocação", value: periodoText(alocacao) }],
        };
        return {
          ...item,
          left: position?.left ?? item.left,
          width: position?.width ?? item.width,
          label: obraLabel(alocacao.obra),
          past: false,
          transferida: true,
          segments: [{ kind: "transferida" as const, left: 0, width: 100 }],
          tooltip: tipCardToText(card),
          card,
        };
      }),
      bands: highlight
        ? [
            {
              key: `${row.key}-destaque`,
              left: highlight.left,
              width: highlight.width,
              tooltip: rangeLine("Período da substituição", alocacao),
              highlight: true,
            },
          ]
        : [],
    };
  });

  return { rows, timeline };
}
